import { getReferenceData } from 'common/reference-data/containers/reference-data-actions';
import { getEquipmentInfoRequest } from 'equipment/info/containers/info-actions';
import { fetchInstalledAtInfo } from 'installed-at/info/containers/installed-at-info-actions';
import { call, fork, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi from 'utils/api';
import moment from 'utils/cops-moment';
import { types as mappingTypes } from 'utils/mappings';
import { viewConfig } from '../../../common/gantt/config';
import * as actions from './details-actions';
import * as types from './details-constants';

export const getServiceCenterId = state => state.navigation.navigationSelf.serviceCenterId;
export const getAllOrderPartnerTypes = state => state.referenceData.data.all_order_partnerTypes;
export const getAllOrderLogisticsFlow = state => state.referenceData.data.all_order_logisticsFlow;
export const getUserId = state => state.user.id;
export const getWipCodesChanged = state => state.order.details.wipCodesChanged;
export function* getOrderDetails(action) {
  const userId = yield select(getUserId);
  const wipCodesChanged = yield select(getWipCodesChanged);
  if (wipCodesChanged && action.payload?.triggeredById && action.payload.triggeredById === userId) {
    yield put(actions.resetWipCodesChanged());
  } else {
    try {
      const { results, requestCancelled } = yield race({
        results: call(callApi, {
          endpoint: `info/order/${action.payload.id}`,
          options: {
            errorMessage: `Could not get order details ${action.payload.id}`,
            retryAction: action,
          },
        }),
        // Check if user redirects to equipment/installedAt info.
        // In that case, stop the order details request to prevent
        // wrong data on the equipment/installedAt info page
        requestCancelled: take([getEquipmentInfoRequest, fetchInstalledAtInfo]),
      });

      if (!requestCancelled) {
        const partnerTypes = yield select(getAllOrderPartnerTypes);
        const serviceCenterId = yield select(getServiceCenterId);
        if (!partnerTypes || action.payload.shouldRefetchPartners) {
          yield put(
            getReferenceData({
              objectType: 'order',
              name: 'partnerTypes',
              urlParams: { orderId: action.payload.id },
              serviceCenterId,
            }),
          );
        }
        const logisticsFlow = yield select(getAllOrderLogisticsFlow);
        if (!logisticsFlow) {
          yield put(
            getReferenceData({
              objectType: 'order',
              name: 'logisticsFlow',
              urlParams: { orderId: action.payload.id },
              serviceCenterId,
            }),
          );
        }
        if (action.payload?.updateOrderDetailsStatus) {
          yield put(actions.getOrderDetailsStatusSuccess(results));
        }
        yield put(actions.getOrderDetailsSuccess({
          ...results,
          resetFields: action.payload.skipLoading,
        }));
      }
    } catch (err) {
      yield put(actions.getOrderDetailsFailure(err));
    }
  }
}

export const generateRequest = (mapping, values, data, notes) => ({
  details: Object.keys(mapping).reduce((acc, key) => {
    const value = values[key];
    const mappedField = mapping[key];
    if (key === 'startDate') {
      // add date and time
      const date = moment(value).format('MM/DD/YYYY');
      const time = moment(values.startTime).format('HH:mm');
      // eslint-disable-next-line max-len
      acc[mappedField] = moment.utc(`${date} ${time}`, 'MM/DD/YYYY HH:mm').format('YYYY-MM-DDTHH:mm:ss[Z]');
    } else if (key === 'indicator') {
      // Do not empty indicator
      if (values[key]) {
        acc[mappedField] = values[key];
      }
    } else if (key === 'serviceNote' || key === 'invoiceNote') {
      if (values[`${key}Changed`]) {
        let originalNote;
        if (key === 'serviceNote') {
          originalNote = notes && notes?.find(
            n => n.noteType === mappingTypes.order.serviceOrderNoteType,
          );
        } else {
          originalNote = notes &&
            notes?.find(n => n.noteType === mappingTypes.order.invoiceTextNoteType);
        }
        if (originalNote) {
          originalNote.noteTexts = values[key]
            .split('\n')
            ?.map(l => ({ textFormat: '', textLine: l }));
        }

        const currentValue = acc[mappedField]?.find(v => v.id === originalNote?.id);
        if (currentValue) {
          acc[mappedField] = acc[mappedField]?.map((i) => {
            if (i.id === originalNote?.id) {
              return originalNote;
            }
            return i;
          });
        } else {
          acc[mappedField] = [...acc[mappedField], originalNote];
        }
      }
    } else {
      acc[mappedField] = values[key];
    }
    return acc;
  }, data),
  taskList: values.taskList,
  subContracts: values.subContracts,
  orderPartList: values.orderPartList,
});

export const getNotes = state => state.order.details.notes;
export function* saveOrder(action) {
  try {
    const mapping = {
      businessType: 'orderType',
      description: 'selectedDescription',
      paidBy: 'paidBy',
      paidByValue: 'paidByValue',
      indicator: 'businessIndicator',
      value: 'businessValue',
      startDate: 'scheduledDate',
      comment: 'internalComment',
      remark: 'additionalRemark',
      projectNumber: 'projectNumber',
      customerPurchaseOrder: 'customerPurchaseOrder',
      sellingValue: 'sellingValue',
      technicianNumber: 'technicianNumber',
      technicianName: 'technicianName',
      visitType: 'visitType',
      name: 'name',
      incoTerms: 'incoTerms',
      statuses: 'statuses',
      sellingValueCurrency: 'sellingValueCurrency',
      serviceNote: 'draftNotes',
      invoiceNote: 'draftNotes',
      plannerGroup: 'plannerGroup',
      plannerGroupDescription: 'plannerGroupDescription',
      salesOfficeNumber: 'salesOfficeNumber',
      salesGroupNumber: 'salesGroupNumber',
      salesOfficeName: 'salesOfficeName',
      salesGroupName: 'salesGroupName',
      salesOfficeId: 'salesOfficeId',
      salesGroupId: 'salesGroupId',
      businessSubType: 'additionalParameter',
      specialTransport: 'specialTransport',
      exemptDeclaration: 'exemptDeclaration',
    };
    const { data, values, status } = action.payload;
    // filter out GUID's
    if (values.taskList && values.taskList.operations) {
      values.taskList.operations?.map((operation) => {
        if (isNaN(operation.operationNumber)) {
          operation.operationNumber = 0;
        }
        return operation;
      });
    }

    const notes = yield select(getNotes);

    // TODO: fix/check save order for updated partlist
    const request = generateRequest(mapping, values, data, notes);

    const { results, navigatedAway, navigatedToNextOrder } = yield race({
      results: call(callApi, {
        endpoint: `info/update/order/${data.id}?status=${status}`,
        options: {
          method: 'PUT',
          body: JSON.stringify(request),
          errorMessage: 'Could not update order',
          retryAction: action,
        },
      }),
      navigatedAway: take('LOCATION_CHANGE'),
      navigatedToNextOrder: take('GET_ORDER_DETAILS_REQUEST'),
    });

    if (!navigatedAway && !navigatedToNextOrder) {
      yield put(actions.saveOrderSuccess(results));
    }
  } catch (err) {
    yield put(actions.saveOrderFailure(err));
  }
}

export function* addSubOrder(action) {
  try {
    const request = {
      ...action.payload,
    };
    const results = yield call(callApi, {
      endpoint: 'order/sub',
      options: {
        method: 'POST',
        body: JSON.stringify(request),
        errorMessage: 'Could not add sub order',
        retryAction: action,
      },
    });
    yield put(actions.addSubOrderSuccess(results));
  } catch (err) {
    yield put(actions.addSubOrderFailure(err));
  }
}

export function* getLinkedPlannings(action) {
  try {
    const { boardId, entityId } = action.payload;
    const results = yield call(callApi, {
      endpoint: `planning/${boardId}/${entityId}/linkedplanning`,
      options: {
        errorMessage: 'Could not fetch linked plannings',
        retryAction: action,
      },
    });
    yield put(actions.getLinkedPlanningsSuccess(results));
  } catch (err) {
    yield put(actions.getLinkedPlanningsFailure(err));
  }
}

export function* doPlanningUpdateOrderSocketEvents(action) {
  try {
    const { triggeredById, orderId, isDeleteEvent = false, entityId } = action.payload;
    const defaultBody = {
      boardId: viewConfig.boardIdTypes.service,
      entityId: orderId,
    };
    const userId = yield select(getUserId);
    const body = isDeleteEvent ? {
      ...defaultBody,
      addToDeletedArray: triggeredById && triggeredById !== userId,
    } :
      {
        ...defaultBody,
        addToUpdatedArray: triggeredById && triggeredById !== userId,
      };
    const results = isDeleteEvent ? {} : yield call(callApi, {
      endpoint:
        `planning/service/${entityId}`,
      options: {
        errorMessage: 'Could not fetch block data',
        retryAction: action,
      },
    });
    yield put(actions.getLinkedPlanningsRequest(body));
    yield put(actions.planningUpdateOrderSocketEventsSuccess({
      ...results,
      ...body,
      id: entityId,
      isSidePanel: true,
    }));
  } catch (err) {
    yield put(actions.planningUpdateOrderSocketEventsFailure(err));
  }
}

function* watchGetOrderDetails() {
  yield takeLatest(
    [types.GET_ORDER_DETAILS_REQUEST, types.GET_ORDER_DETAILS_STATUS_REQUEST],
    getOrderDetails,
  );
}

function* watchSaveOrder() {
  yield takeLatest(types.SAVE_ORDER_REQUEST, saveOrder);
}

function* watchAddSubOrder() {
  yield takeLatest(types.ADD_SUBORDER_REQUEST, addSubOrder);
}

function* watchGetLinkedPlannings() {
  yield takeLatest(types.GET_LINKED_PLANNINGS_REQUEST, getLinkedPlannings);
}

function* watchPlanningUpdateOrderSocketEvents() {
  yield takeEvery(types.PLANNING_UPDATE_ORDER_SOCKET_EVENTS, doPlanningUpdateOrderSocketEvents);
}

export default [
  fork(watchGetOrderDetails),
  fork(watchSaveOrder),
  fork(watchAddSubOrder),
  fork(watchGetLinkedPlannings),
  fork(watchPlanningUpdateOrderSocketEvents),
];
